import { BUILD_ENV, STAGE, STAGES } from './stage'

const { version } = require('../../package.json')

// Gemeinsame Bausteine fuer Slack-Alerts aus dem Dashboard (Payment-Alert,
// Pricing). Bewusst ohne Import aus helpers/index.js, das zieht aws-amplify
// (ESM) und bricht Jest. Versand ueber sendSlackMessage und die
// Bugsnag-Meldung bleiben deshalb beim Aufrufer.

export const DEFAULT_CONTEXT_TRIGGER =
  'ausgelöst durch einen Dashboard-Aufruf des Kunden'

// ------------------------------------------------------------------ Kopfzeile

// Badge aus STAGE (Backend), nicht aus BUILD_ENV (Branchname). Non-Production
// laeuft in denselben Kanal und muss deshalb schon in der Kopfzeile als
// Testmeldung lesbar sein. Production bekommt kein Praefix, damit der
// Normalfall ohne Rauschen bleibt.
export const stagePrefix = () =>
  STAGE === STAGES.PRODUCTION ? '' : `[${STAGE.toUpperCase()} · TESTMELDUNG] `

// Zweites, redundantes Signal. Schon das Emoji unterscheidet echt von Test.
export const headlineEmoji = (productionEmoji) =>
  STAGE === STAGES.PRODUCTION ? productionEmoji : ':construction:'

/**
 * Fusszeile fuer den context-Block.
 * @param {string|null} [trigger] Wer oder was den Alert ausgeloest hat. Ein
 *   leerer Wert oder null laesst den Teil weg.
 */
export const contextLine = (trigger = DEFAULT_CONTEXT_TRIGGER) =>
  [
    `Stage: ${STAGE}`,
    // Der Branchname sagt nichts ueber das Backend (siehe ./stage), gehoert
    // aber als Debug-Hinweis dazu, sobald er von der Stage abweicht.
    BUILD_ENV === STAGE ? null : `Branch: ${BUILD_ENV}`,
    `Dashboard v${version}`,
    trigger
  ]
    .filter(Boolean)
    .join('  ·  ')

// ------------------------------------------------------------------- Cooldown

// Standard, wenn der Aufrufer keinen Speicher mitgibt. Fehler faengt der Store
// selbst ab. Wer sie an Bugsnag melden will, gibt den Wrapper aus
// ./local-storage als storage mit.
const browserStorage = {
  getItem: (key) =>
    typeof window === 'undefined' ? null : window.localStorage.getItem(key),
  setItem: (key, value) => {
    if (typeof window !== 'undefined') window.localStorage.setItem(key, value)
  }
}

/**
 * Merkt sich je Key, wann zuletzt gesendet wurde, und haelt Dopplungen im
 * Kanal fuer cooldownMs zurueck.
 *
 * Zwei Ebenen. localStorage traegt den Cooldown ueber Reloads und Tabs. Die
 * Session-Map ist die zweite Verteidigungslinie und haelt auch, wenn
 * localStorage tot ist (Safari Private Mode, QuotaExceeded). Sie gilt dann
 * nur fuer diese Store-Instanz, also praktisch fuer diesen Tab.
 *
 * @param {object} options
 * @param {string} options.storageKey localStorage-Key des Records
 * @param {number} options.cooldownMs
 * @param {{getItem: Function, setItem: Function}} [options.storage]
 * @returns {{
 *   wasSentRecently: (key: string, now?: number) => boolean,
 *   markSent: (key: string, now?: number) => void,
 *   unmark: (key: string, now?: number) => void
 * }}
 */
export const createCooldownStore = ({
  storageKey,
  cooldownMs,
  storage = browserStorage
}) => {
  const session = new Map()

  const isFresh = (sentAt, now) =>
    typeof sentAt === 'number' && now - sentAt < cooldownMs

  const read = () => {
    try {
      const raw = storage.getItem(storageKey)

      if (!raw) return {}

      const parsed = JSON.parse(raw)

      return parsed && typeof parsed === 'object' ? parsed : {}
    } catch {
      // Korrupter Eintrag wird wie leer behandelt. Bewusst fail-open, denn
      // eine Dopplung im Kanal ist harmloser als ein verschluckter Alert.
      return {}
    }
  }

  const write = (record, now) => {
    // Beim Schreiben auf den Cooldown kuerzen, sonst waechst der Record
    // unbegrenzt.
    const pruned = Object.fromEntries(
      Object.entries(record).filter(([, sentAt]) => now - sentAt < cooldownMs)
    )

    try {
      storage.setItem(storageKey, JSON.stringify(pruned))
    } catch {
      // Toter localStorage. Die Session-Map traegt den Cooldown weiter.
    }
  }

  const wasSentRecently = (key, now = Date.now()) =>
    isFresh(session.get(key), now) || isFresh(read()[key], now)

  const markSent = (key, now = Date.now()) => {
    session.set(key, now)
    write({ ...read(), [key]: now }, now)
  }

  // Fuer den Fehlerfall. Schlaegt der Versand fehl, darf der naechste Versuch
  // nicht am Cooldown haengen bleiben.
  const unmark = (key, now = Date.now()) => {
    session.delete(key)

    const record = read()

    delete record[key]
    write(record, now)
  }

  return { wasSentRecently, markSent, unmark }
}
